import React, { useState, useRef, useEffect } from 'react'
import Table from '.'
import axios from 'axios'
import { SvgCheck, SvgDownload, SvgGreenCheck, SvgReceipt } from '../svg'
import PropTypes from 'prop-types'
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate, useParams } from "react-router-dom";
// import { ExportStatus } from '../status'
import { convertPageToOffsetLimit } from '../../utils';
import { API_DEFAULT_PAGE_SIZE } from '../../const';
import { getExports, duplicateExport } from '../../slices/export';
import { browserHistory } from 'react-router'
import moment from 'moment';
import { SvgSelectArrow } from '../svg'
import { SvgExport } from '../svg'
import { SvgSubscriptions } from '../svg'

const translations = [
    gettext('Title'),
    gettext('Date / time'),
    gettext('Type'),
    gettext('User'),
    gettext('Status'),
    gettext('Downloaded?'),
    gettext('Download'),
    gettext('Run again'),
    gettext('No exports found.'),
    gettext('You can create new exports under each respective component, e.g. go to the Subscriptions list, filter the list if you want, and then click "Export". It is possible to export subscriptions, customers and transactions.'),
]

export const ExportTable = () => {
    let { exportId } = useParams();

    const dispatch = useDispatch()
    const navigate = useNavigate();

    const exports = useSelector((state) => state.export)
    const [currentPage, setCurrentPage] = useState(1)
    const [pageSize, setPageSize] = useState(API_DEFAULT_PAGE_SIZE)
    const [isDirty, setIsDirty] = useState(true)

    const exportInfo = useSelector((state) => state.export.exportInfo);
    const exportInfoTotalRef = useRef()

    const fetchExportsPromise = useRef()

    const handleDownload = (url) => {
        axios.get(url, {
            responseType: 'arraybuffer',
            headers: {
                'Content-type': 'application/json'
            }
        })
            .then((response) => {
                let userAgent = window.navigator.userAgent;
                const type = response.headers['content-type']
                let headerLine = response.headers['content-disposition'];
                const blob = new Blob([response.data], { type: type, encoding: 'UTF-8' })
                const link = document.createElement('a')

                if (userAgent.match(/iPad/i) || userAgent.match(/iPhone/i)) { // safari & opera on ios
                    let url = window.URL.createObjectURL(blob);
                    window.location.href = url;
                }
                window.URL = window.URL || window.webkitURL;
                link.href = window.URL.createObjectURL(blob)
                // Get filename from response
                let startFileNameIndex = headerLine.indexOf('"') + 1
                let endFileNameIndex = headerLine.lastIndexOf('"');
                let filename = headerLine.substring(startFileNameIndex, endFileNameIndex);
                link.download = `${filename}`
                link.click()
                setIsDirty(true)
            })
    }

    const handleDuplication = (id) => {
        Promise.resolve(dispatch(duplicateExport({ exportId: id })))
            .then((response) => {
                setIsDirty(true)
            })
    }

    const columns = React.useMemo(
        () => [
            {
                Header: gettext('Title'),
                accessor: 'export_title',
            },
            {
                Header: gettext('Date / time'),
                accessor: 'added_at',
                className: "text-center",
                Cell: ({ value }) => {
                    return <div><b>{moment(value).format('DD.MM.yyyy')}</b>&nbsp;{moment(value).format('HH:mm')}</div>
                }
            },
            {
                Header: gettext('Type'),
                accessor: 'export_type',
                Cell: ({ value }) => {
                    return gettext(value + 's')
                },
            },
            {
                Header: gettext('User'),
                accessor: 'user_name',
            },
            {
                Header: gettext('Status'),
                accessor: 'status',
                Cell: ({ value }) => {
                    return gettext(value)
                },
            },
            {
                Header: gettext('Downloaded?'),
                accessor: 'downloaded',
                Cell: ({ value }) => {
                    if (value === true) {
                        return (<div className="text-center">
                            <SvgGreenCheck />
                        </div>);
                    }
                    return ''
                }
            },
            {
                Header: gettext('Download'),
                accessor: 'id',
                Cell: ({ row, value }) => {
                    if (row.original.status === 'completed') {
                        return (
                            <div className="text-center">
                                <div onClick={() => handleDownload(`/apiv2/exports/${value}/download`)} className="text-center"><SvgDownload /></div>
                            </div>);
                    }
                    return ''
                }
            },
            {
                Header: gettext('Run again'),
                accessor: 'copy',
                Cell: ({ row, value }) => {
                    if (row.original.status === 'completed') {
                        return (
                            <div className="text-center">
                                <div onClick={() => handleDuplication(row.original.id)} className="text-center"><SvgSubscriptions /></div>
                            </div>);
                    }
                    return ''
                }
            }
        ])

    const onRowClick = (row) => {
        navigate(`/export/${row.original.id}`)
    }

    useEffect(() => {
        setIsDirty(true)
    }, [currentPage])
    useEffect(() => {
        setIsDirty(true)
    }, [pageSize])


    useEffect(() => {
        exportInfoTotalRef.current = exportInfo.totalUnfetched
    }, [exportInfo.totalUnfetched])

    useEffect(() => {
        if (exportInfoTotalRef.current !== exportInfo.totalUnfetched) {
            setIsDirty(true)
        }
    }, [exportInfo.totalUnfetched])

    useEffect(() => {
        if (isDirty) {
            const offsetLimit = convertPageToOffsetLimit(currentPage, pageSize)
            const params = [...offsetLimit]

            fetchExportsPromise.current?.abort()
            let ignore = false
            if (!ignore) {
                fetchExportsPromise.current = dispatch(getExports(params))
                setIsDirty(false)
            }
            return () => {
                ignore = true;
            };
        }
    }, [isDirty])

    return (
        <>
            <div className="row mt-3">
                <div className="col-12 overflow-scroll">
                    {!exports.loading && exports.results.length === 0 ?
                        <div className="alert alert-primary" role="alert">
                            <p>{gettext('No exports found.')}</p>
                            <p>{gettext('You can create new exports under each respective component, e.g. go to the Subscriptions list, filter the list if you want, and then click "Export". It is possible to export subscriptions, customers and transactions.')}</p>
                        </div>
                        :
                        <Table
                            columns={columns}
                            data={exports.results}
                            onRowClick={onRowClick}
                            selectedRow={exportId}
                            loading={exports.loading}
                            currentPage={currentPage}
                            setCurrentPage={setCurrentPage}
                            pageChangeHandler={(page, pageSize) => {
                                setCurrentPage(page)
                                setPageSize(pageSize)
                            }}
                        />
                    }
                </div>
            </div>

        </>
    )
}

ExportTable.propTypes = {
    data: PropTypes.array,
    size: PropTypes.string
};

ExportTable.defaultProps = {
    data: [],
    size: 'md'
};